/* cf-panel-variants.js — Click Fencing, 2026-09-03
 *
 * KILLS THE "Quote Total - Quote Summary: × 128808" LINE.
 *
 * Pete, 2026-09-03: "remove the 128,808 line and quote summary x 1. just fix
 * it. it has to change each time for every different order."
 *
 * WHAT THAT LINE ACTUALLY IS. Every calculator order is added to the cart as a
 * single line: the unlisted "Quote Total" variant at $0.01, quantity = the
 * whole quote in cents. cf-real-line-items.js then rebuilds that into real
 * products and shrinks the $0.01 line to whatever it could NOT resolve. On a
 * glass order that leftover is the glass itself, so a $747.99 quote checks out
 * as "Quote Total × 38397" plus a spigot line, and the order notification, the
 * customer email and the picking list all show a nonsense quantity.
 *
 * WHY THE GLASS NEVER RESOLVED. Measured 2026-09-02 on sessionStorage.ullc_quote:
 *     { item:"1450mm Glass Panels", unitPrice:126.54, variantId: null  }   <-- glass
 *     { item:"1500mm Glass Panel",  unitPrice:130.89, variantId: null  }   <-- glass
 *     { item:"Core Drill Spigot …", unitPrice:60.67,  variantId: 41519272853598 }
 * Hardware carries a variantId and therefore becomes a real line; panels carry
 * null and therefore cannot. quote-review.js only emits a "[vid:…]" tag when
 * the breakdown entry has one, so the whole chain downstream is starved.
 *
 * WHAT THIS DOES. Fills in the missing variantId, and nothing else. It never
 * overwrites an id that is already there, never touches a price, a quantity or
 * a total, and if it cannot identify something with certainty it leaves it
 * alone — the old balance line still covers that case, so a miss costs nothing.
 *
 * HOW IT IDENTIFIES A PANEL — WIDTH **AND** PRICE, BOTH, ALWAYS.
 * Width alone is not enough: a 1450mm panel exists as plain (SKU 48201,
 * $126.54) and EnduroShield coated (49590, $177.55), and the breakdown text
 * says "1450mm Glass Panels" either way. Matching the unit price as well picks
 * the right one with no guesswork and no need to know whether the customer
 * ticked the coating. Same for raked and hinge panels, which each exist in
 * plain/coated and standard/soft-close variations at different prices.
 * If width+price does not match exactly one variant, it returns null.
 *
 * Data comes from /products.json — the same public feed cf-sku-prices.js uses —
 * because window.glassCalculator does NOT exist on /pages/quote-review, only on
 * the calculator pages. Fetched once and cached.
 *
 * ENRICHING THE STORED QUOTE ON quote-review IS TOO LATE ON ITS OWN. Measured:
 * that page reads the quote into memory at load, so a later write to
 * sessionStorage is ignored. The calculator hook below is what actually fixes
 * it - the quote is saved WITH the ids and quote-review inherits them. The
 * stored-quote pass is kept only to repair quotes saved before this shipped.
 */
(function () {
  'use strict';

  var TAG = '[cf-panel-variants]';
  var cache = null;
  var loading = null;

  /* Product handles by the kind of thing the breakdown is describing. Read off
     the live catalogue rather than hard-coded ids, so a re-created product does
     not silently break this. */
  var HANDLE_MATCH = {
    rake:  /raked-panel/i,
    hinge: /hinge-panel/i,
    gate:  /gate-panel|glass-gate|gate$/i,
    panel: /^frameless-glass-panel$|enduroshield-clear-safety-toughened-frameless-glass-panel/i,
  };

  function loadCatalogue() {
    if (cache) return Promise.resolve(cache);
    if (loading) return loading;
    loading = fetch('/products.json?limit=250')
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var groups = { panel: [], rake: [], hinge: [], gate: [] };
        (j.products || []).forEach(function (p) {
          Object.keys(HANDLE_MATCH).forEach(function (kind) {
            if (HANDLE_MATCH[kind].test(p.handle)) groups[kind].push(p);
          });
        });
        cache = groups;
        return cache;
      })
      .catch(function (e) { console.warn(TAG, 'catalogue load failed', e); loading = null; return null; });
    return loading;
  }

  /* Order matters: "1500mm Gate Hinge Panel" is a HINGE panel, not a gate. */
  function kindOf(text) {
    var t = String(text || '');
    if (/rake/i.test(t)) return 'rake';
    if (/hinge/i.test(t)) return 'hinge';
    if (/gate/i.test(t)) return 'gate';
    if (/glass panel/i.test(t)) return 'panel';
    return null;
  }

  function widthOf(text) {
    var m = String(text || '').match(/(\d{3,4})\s*mm/);
    return m ? m[1] : null;
  }

  function resolve(groups, item, unitPrice) {
    try {
      if (!groups) return null;
      var kind = kindOf(item);
      var w = widthOf(item);
      if (!kind || !w) return null;
      var want = Number(unitPrice);
      if (!isFinite(want) || want <= 0) return null;
      var wanted = want.toFixed(2);
      var hits = [];
      (groups[kind] || []).forEach(function (p) {
        (p.variants || []).forEach(function (v) {
          var vw = String(v.title || '').match(/(\d{3,4})/);
          if (vw && vw[1] === w && Number(v.price).toFixed(2) === wanted) {
            hits.push(v.id);
          }
        });
      });
      // Exactly one match or nothing. Two products at the same width AND the
      // same price would be genuinely ambiguous, and a wrong part is far worse
      // than a balance line.
      if (hits.length === 1) return hits[0];
      if (hits.length > 1) console.warn(TAG, 'ambiguous', item, unitPrice, hits.length);
      return null;
    } catch (e) { console.warn(TAG, 'resolve', e); return null; }
  }

  /* Fills variantId in place on a breakdown array. Returns how many it set. */
  function enrich(groups, breakdown) {
    if (!Array.isArray(breakdown)) return 0;
    var n = 0;
    breakdown.forEach(function (row) {
      if (!row || row.variantId) return;                 // never overwrite
      var id = resolve(groups, row.item, row.unitPrice);
      if (id) { row.variantId = id; n++; }
    });
    return n;
  }

  function enrichStoredQuote(groups) {
    var total = 0;
    try {
      var raw = sessionStorage.getItem('ullc_quote');
      if (raw) {
        var q = JSON.parse(raw);
        var n = enrich(groups, q.breakdown);
        if (n) { sessionStorage.setItem('ullc_quote', JSON.stringify(q)); total += n; }
      }
    } catch (e) { console.warn(TAG, 'active quote', e); }
    try {
      var rawAll = localStorage.getItem('ullc_quotes');
      if (rawAll) {
        var all = JSON.parse(rawAll);
        var m = 0;
        var list = Array.isArray(all) ? all : Object.keys(all).map(function (k) { return all[k]; });
        list.forEach(function (q) { if (q) m += enrich(groups, q.breakdown); });
        if (m) { localStorage.setItem('ullc_quotes', JSON.stringify(all)); total += m; }
      }
    } catch (e) { console.warn(TAG, 'saved quotes', e); }
    return total;
  }

  /* On a calculator page the breakdown is rebuilt constantly, so enrich the
     live one after every recalculation as well as the stored copy. Glass is the
     one calculator whose instance is reachable. */
  function hookCalculator(groups) {
    try {
      var gc = window.glassCalculator || window.glassFencingCalculator;
      if (!gc) return;
      var proto = Object.getPrototypeOf(gc);
      if (!proto || proto.__cfPanelVariantsPatched) return;
      ['updateQuoteBreakdown', 'updateQuoteDisplay', 'updateComponentsQuote'].forEach(function (m) {
        if (typeof proto[m] !== 'function') return;
        var orig = proto[m];
        proto[m] = function () {
          var out = orig.apply(this, arguments);
          try {
            if (this.state && this.state.quoteBreakdown) enrich(groups, this.state.quoteBreakdown);
          } catch (e) { console.warn(TAG, m, e); }
          return out;
        };
      });
      proto.__cfPanelVariantsPatched = true;
      try { if (gc.state && gc.state.quoteBreakdown) enrich(groups, gc.state.quoteBreakdown); } catch (e) {}
      console.log(TAG, 'calculator hooked');
    } catch (e) { console.warn(TAG, 'hookCalculator', e); }
  }

  function run() {
    loadCatalogue().then(function (groups) {
      if (!groups) return;
      hookCalculator(groups);
      var n = enrichStoredQuote(groups);
      if (n) console.log(TAG, 'filled ' + n + ' variant id(s)');
    });
  }

  run();
  // The quote can be swapped after load (picking another saved quote), and the
  // calculator boots late on some pages, so sweep a few more times. Cheap:
  // enrich() only writes when a variantId is missing.
  var ticks = 0;
  var iv = setInterval(function () {
    ticks++;
    run();
    if (ticks >= 10) clearInterval(iv);
  }, 1500);
})();
